import {
  CapabilityPage,
  type CapabilitySection,
} from "@/components/marketing/CapabilityPage";

/**
 * ``/features/napojeni-a-rozsireni`` — the modularity story: what stays
 * where it already is.
 *
 * Nine of the seventeen items are gated, the most of any page here, so
 * this is where the live badge earns its keep. It also carries the two
 * claims this section was written to stop over-selling:
 *
 * * **Connectors are described by what they do, not by how many there
 *   are** (owner's direction 2026-09-18). The catalogue is the live
 *   list and it changes; naming today's one connector on a marketing
 *   page would age into either a lie or a boast about being small.
 *   Nothing here promises a connector the catalogue does not have —
 *   that was the defect KAN-1461 removed from the pricing page.
 * * **The sandbox copies the catalogue and the look, never customer
 *   data** (``app/services/sandbox.py``). The copy says so, because a
 *   reader who assumes "a copy of my shop" means their orders too will
 *   test against data that is not there.
 *
 * ``api`` and ``identity`` carry ``audience: "it"``: they need somebody
 * technical, and the 2026-10-07 audit (#68) found nothing on the page
 * telling an owner without one that those sections can be skipped.
 *
 * The ink block closes the page on ``safety``: trying an integration
 * before it touches live orders is the sentence an IT-shy owner needs
 * to actually read.
 */
const SECTIONS: readonly CapabilitySection[] = [
  { id: "yourSite", items: [{ id: "snippet" }, { id: "funnel" }], shot: "integrations-yourSite" },
  {
    id: "apps",
    items: [
      { id: "catalog" },
      { id: "connectors", entitlement: "integrations.marketplace" },
      { id: "health", entitlement: "integrations.marketplace" },
    ],
    shot: "integrations-apps",
  },
  {
    id: "api",
    audience: "it",
    items: [
      // Reading needs only a token, and tokens are a COUNT (``max_api_tokens``),
      // not the ``api.write`` key — only writing is (``require_api_scope``,
      // ``POST /admin/api-tokens``). Badged off the limit, the card names the
      // same plan as the pricing table's „API tokeny“ row (KAN-1949, #55).
      { id: "read", limit: "max_api_tokens" },
      { id: "tokens", limit: "max_api_tokens" },
      { id: "machines", entitlement: "api.write" },
      { id: "webhooks", entitlement: "webhooks" },
    ],
    shot: "integrations-api",
  },
  {
    id: "identity",
    audience: "it",
    items: [
      { id: "oidc", entitlement: "sso.oidc" },
      { id: "saml", entitlement: "sso.saml" },
      { id: "scim", entitlement: "sso.scim" },
      { id: "audit", entitlement: "audit_log" },
    ],
    shot: "integrations-identity",
  },
  {
    id: "safety",
    tone: "ink",
    items: [{ id: "sandbox", entitlement: "sandbox" }, { id: "export" }],
    shot: "integrations-safety",
  },
];

const PAINS = ["form", "retype", "access"] as const;
const GAINS = ["night", "once", "offboard"] as const;

export default function FeaturesIntegrations(): JSX.Element {
  return (
    <CapabilityPage
      i18nKey="features.pages.integrations"
      path="/features/napojeni-a-rozsireni"
      pains={PAINS}
      gains={GAINS}
      sections={SECTIONS}
    />
  );
}
